<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { onMounted, ref, watch } from 'vue'
import DailyQuestionImage from '@/assets/images/gifs/question-mark.gif'
import { T, useTranslate } from '@tolgee/vue'
import { useDailyQuestion } from '@/composables/game/daily'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import BaseInput from '@/components/base/BaseInput.vue'
import { useToaster } from '@/composables/game/toasters'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { isiOS } from '@/helpers/browser'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const WebApp = Telegram.WebApp
const { claimed, getQuestion, reward, answerSuccess, sendAnswer } = useDailyQuestion()
const { notify } = useToaster()
const { startCoinAnimation } = useFlyingCoins()
const isIOSBrowser = isiOS()
const { t } = useTranslate()
const { addHapticFeedback } = useHapticFeedback()

const loadingClaim = ref<boolean>(false)
const hasError = ref<boolean>(false)

const question = ref<string>('')
const answer = ref<string>('')

async function claim() {
  if (claimed.value) {
    emit('hide')
    return
  }
  if (answer.value.length) {
    loadingClaim.value = true
    await sendAnswer(answer.value)
    loadingClaim.value = false
  }
}

function pushUpModal() {
  if (!isIOSBrowser || Number(WebApp.version.split('.')[0]) >= 9)  return
  const modal = document.querySelector('#base-modal') as HTMLElement | undefined
  if (!modal) return
  modal.style.bottom = '265px'
}

function pushDownModal() {
  if (!isIOSBrowser || Number(WebApp.version.split('.')[0]) >= 9)  return
  const modal = document.querySelector('#base-modal') as HTMLElement | undefined
  if (!modal) return
  modal.style.bottom = '0px'
}

onMounted(async () => {
  question.value = await getQuestion()
})

watch(loadingClaim, () => {
  if (!loadingClaim.value) {
    hasError.value = !answerSuccess.value
    if (answerSuccess.value) {
      startCoinAnimation()
      addHapticFeedback()
      notify({ title: t.value('task.completed', 'Success!'), type: 'success' })
    }
  }
})

watch(answer, () => {
  if (hasError.value) {
    hasError.value = false
  }
})

</script>
<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
    content-class="overflow-hidden flex flex-col px-0"
    outer-content-class="overflow-hidden"
    border-container-class="overflow-hidden"
  >
    <div class="h-full flex flex-col items-center justify-between text-center overflow-auto px-6 gap-y-8">
      <div
        class="w-full flex flex-col items-center gap-y-6"
      >
        <img
          class="rounded-full w-[120px] h-[120px] min-w-[120px] object-cover"
          :src="DailyQuestionImage"
        >
        <div class="flex flex-col items-center">
          <h1 class="text-center text-2xl font-bold -tracking-[0.48px] text-light-white">
            <T
              key-name="tasks.daily_question.title"
              default-value="Question of the day"
            />
          </h1>
          <p class="font-medium text-white/60 text-base selectable-text">
            {{ question }}
          </p>
        </div>
        <NumberFormatTooltip
          :number="reward"
          :formatting-type="'full'"
        >
          <div
            class="flex items-center gap-x-2"
          >
            <img
              :src="CoinIcon"
              class="size-8 min-w-8"
            >
            <span
              class="text-xl leading-6 font-semibold -tracking-[0.4px] text-teal-primary"
            >+{{ formatNumberFullTruncate(reward) }}</span>
          </div>
        </NumberFormatTooltip>
        <div
          v-if="!claimed"
          class="flex flex-col items-center w-full gap-y-2"
        >
          <BaseInput
            v-model="answer"
            placeholder="Enter answer here"
            :has-error="hasError"
            @focus="pushUpModal"
            @blur="pushDownModal"
          />
          <p
            :class="twMerge('text-sm leading-6 font-medium text-white opacity-70 -tracking-[0.28px]', hasError && 'text-red-danger')"
          >
            <T
              v-if="hasError"
              key-name="tasks.daily_question.not_correct"
              default-value="This answer is not correct. Try again"
            />
            <T
              v-else
              key-name="tasks.daily_question.answer_english"
              default-value="Write the answer in English"
            />
          </p>
        </div>
      </div>
      <BaseButton
        style-type="primary-pink"
        class="w-full"
        :disabled="(!claimed && !answer.length)"
        :loading="loadingClaim"
        @click="claim"
      >
        <T
          v-if="!claimed"
          key-name="tasks.daily_question.check_answer"
          default-value="Check answer"
        />
        <T
          v-else
          key-name="tasks.daily_question.come_back"
          default-value="Come back tomorrow"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>
